import { gql, useApolloClient } from "@apollo/client";
import { faArrowAltCircleUp } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getDataOrNull } from "@msys/common";
import {
  CardItem,
  DataGrid,
  DebouncedSearchInput,
  ErrorMessage,
  GridColDef,
  ListHeader,
  LoadingSpinner,
  ModalOpenButton,
  SelectMultiple,
  useFormatting,
} from "@msys/ui";
import { Check as CheckIcon } from "@mui/icons-material";
import {
  Button,
  Icon,
  IconButton,
  Stack,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import { omitBy } from "lodash-es";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Avatar } from "../../commons/Avatar.js";
import { ButtonCreate } from "../../commons/button/Button.js";
import { SwitchCollectionViewButton } from "../../commons/button/SwitchCollectionViewButton.js";
import { FilterButton } from "../../commons/filters/FilterButton.js";
import { FilterChipGroup } from "../../commons/filters/FilterChip.js";
import { FilterModal } from "../../commons/filters/FilterModal.js";
import { useFiltersAndPagination } from "../../commons/filters/useFiltersAndPagination.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../commons/hooks/useCollectionView.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import {
  PAGE_LIST_RESULTS_PER_PAGE,
  RESULTS_PER_PAGE_OPTIONS,
} from "../../constants.js";
import { CrmCompanyListItem } from "../../features/crm-companies/components/CrmCompanyListItem.js";
import { useCrmOrganisationAbcCategories } from "../../features/crm-companies/hooks/useCrmOrganisationAbcCategories.js";
import { CrmCompanyInviteModal } from "../../features/crm-companies/modals/CrmCompanyInviteModal.js";
import { CrmCreateModal } from "../../features/crm/CrmCreateModal.js";
import { CrmTagChips } from "../../features/crm/CrmTagChips.js";
import { CrmTagsAutocompleteInput } from "../../features/crm/CrmTagsAutocompleteInput.js";
import { FilterCrmTagChipGroup } from "../../features/crm/FilterCrmTagChipGroup.js";
import { MassImportCrmModal } from "../../features/crm/MassImportCrmModal.js";
import { OrganisationCategoryAvatar } from "../../features/organisations/OrganisationCategoryAvatar.js";
import { useOrganisationTags } from "../../features/organisations/useOrganisationTags.js";
import { PdsSyncBanner } from "../../features/pds-sync/PdsSyncBanner.js";
import { PdsSyncToolbarButton } from "../../features/pds-sync/PdsSyncToolbarButton.js";
import { usePdsSyncReviewAccess } from "../../features/pds-sync/usePdsSyncReviewAccess.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import {
  namedOperations,
  OrganisationCrmOrganisationsSortBy,
  OrganisationCrmOrganisationsSorting,
} from "../../../clients/graphqlTypes.js";
import {
  CrmAllQueryVariables,
  CrmCompanies__CrmCompanyFragment,
  useCrmAllQuery,
} from "./CrmAll.generated.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";

type Filters = Omit<CrmAllQueryVariables, "limit" | "offset" | "sorting">;
const DEFAULT_SORTING: OrganisationCrmOrganisationsSorting[] = [
  {
    column: "organisationName",
    direction: "asc",
  },
];

interface Props {
  submenuItems: PageTopbarItem[];
}

export function CrmAll({ submenuItems }: Props) {
  const { t } = useTranslate(["CrmOrganisations", "Global", "CrmPageTopbar"]);
  const navigate = useNavigate();

  const pdsSyncAccess = usePdsSyncReviewAccess();

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>(
      "crm-companies",
      "table",
      "list"
    );

  const {
    offset,
    limit,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
  } = useFiltersAndPagination<OrganisationCrmOrganisationsSorting, Filters>(
    {},
    DEFAULT_SORTING,
    PAGE_LIST_RESULTS_PER_PAGE
  );

  const QUERY_BASE_VARIABLES = {
    offset,
    limit,
    sorting: DEFAULT_SORTING,
  };

  const client = useApolloClient();
  const query = useCrmAllQuery({
    client,
    variables: {
      ...QUERY_BASE_VARIABLES,
      ...omitBy(filters, filter => (filter ? filter.length === 0 : true)),
      sorting,
    },
  });

  const totalCount =
    getDataOrNull((query.data ?? query.previousData)?.crmCompanies)
      ?.totalCount ?? 0;
  const crmCompanies =
    getDataOrNull((query.data ?? query.previousData)?.crmCompanies)?.edges.map(
      edge => edge.node
    ) ?? [];
  const { tags } = useOrganisationTags("CRM_COMPANY");

  const renderInviteButton = React.useCallback(
    (crmCompany: CrmCompanies__CrmCompanyFragment) =>
      crmCompany.linkedSystemOrganisationId ? (
        <Tooltip
          title={t("Invitation accepted", {
            ns: "CrmOrganisations",
          })}
        >
          <CheckIcon fontSize="small" color="primary" />
        </Tooltip>
      ) : crmCompany.isInvited ? (
        <Button size="extra-small" variant="outlined" color="primary" disabled>
          {t("Invited", { ns: "CrmOrganisations" })}
        </Button>
      ) : (
        <ModalOpenButton
          Modal={CrmCompanyInviteModal}
          modalProps={{
            crmCompanyId: crmCompany.id,
            refetchQueries: [namedOperations.Query.CrmAll],
          }}
        >
          <Button
            size="extra-small"
            variant="outlined"
            color="primary"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {t("Invite", { ns: "CrmOrganisations" })}
          </Button>
        </ModalOpenButton>
      ),
    [t]
  );

  return (
    <Page
      title={t("Businesses & Groups", { ns: "CrmPageTopbar" })}
      submenuItems={submenuItems}
    >
      <PageContainer>
        <PdsSyncBanner access={pdsSyncAccess} defaultEntityType="COMPANY" />
        <ListHeader
          SwitchViewButton={
            <SwitchCollectionViewButton
              allowedViews={ALLOWED_VIEWS}
              activeView={activeView}
              setActiveView={setActiveView}
            />
          }
          CreateButton={
            <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_CRM">
              <ModalOpenButton
                Modal={CrmCreateModal}
                modalProps={{
                  title: t("New contact", {
                    ns: "CrmOrganisations",
                  }),
                  handleComplete: async (handleClose, organisation) => {
                    handleClose();
                    navigate(`/crm/organisations/${organisation.id}`);
                  },
                }}
              >
                <ButtonCreate
                  variant="contained"
                  color="primary"
                  //title={t("CrmOrganisations::Create contact")}
                  title={t("New contact", {
                    ns: "CrmOrganisations",
                  })}
                />
              </ModalOpenButton>
            </RestrictedByOrganisationPermissionWithDebug>
          }
          FilterButton={
            <ModalOpenButton
              Modal={CrmAllFilterModal}
              modalProps={{
                filters,
                setFilters,
                resetFilters,
                tags,
              }}
            >
              <FilterButton />
            </ModalOpenButton>
          }
          FilterChips={
            query.variables?.filterByTagsAny?.length ||
            query.variables?.filterAbcCategory?.length ? (
              <Stack
                direction={"row"}
                spacing={1}
                sx={{
                  flexWrap: "wrap",
                }}
              >
                <FilterCrmTagChipGroup
                  // label={t("Tag", {
                  //   ns: "CrmOrganisations",
                  // })}
                  values={filters.filterByTagsAny}
                  setValues={value =>
                    setFilters(state => ({
                      ...state,
                      filterByTagsAny: value,
                    }))
                  }
                />
                <FilterChipGroup
                  label={t("Priority", {
                    ns: "CrmOrganisations",
                  })}
                  values={filters.filterAbcCategory}
                  getValueLabel={value => value.toUpperCase()}
                  setValues={value =>
                    setFilters(state => ({
                      ...state,
                      filterAbcCategory: value,
                    }))
                  }
                />
              </Stack>
            ) : undefined
          }
          SearchField={
            <DebouncedSearchInput
              placeholder={t("Search", {
                ns: "Global",
              })}
              defaultValue={filters.filterSearchTerm}
              onChangeSearchTerm={newValue =>
                setFilters(filters => ({
                  ...filters,
                  filterSearchTerm: newValue,
                }))
              }
            />
          }
          ActionButtons={
            <Stack direction={"row"} spacing={1} sx={{ alignItems: "center" }}>
              {pdsSyncAccess.isEnabled && (
                <PdsSyncToolbarButton
                  access={pdsSyncAccess}
                  defaultEntityType="COMPANY"
                />
              )}
              <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_CRM">
                <ModalOpenButton Modal={MassImportCrmModal}>
                  <IconButton size="small" color="primary">
                    <Tooltip
                      title={t("Import crm data", {
                        ns: "CrmOrganisations",
                      })}
                    >
                      <Icon>
                        <FontAwesomeIcon icon={faArrowAltCircleUp} />
                      </Icon>
                    </Tooltip>
                  </IconButton>
                </ModalOpenButton>
              </RestrictedByOrganisationPermissionWithDebug>
            </Stack>
          }
          sx={{ marginBottom: 2 }}
        />
        <CrmCompanyCollectionView
          activeView={activeView}
          isPdsSyncEnabled={pdsSyncAccess.isEnabled}
          isLoading={query.loading}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          sorting={sorting}
          setSorting={setSorting}
          crmCompanies={crmCompanies}
          totalCount={totalCount}
          onItemClick={item => navigate(`/crm/organisations/${item.id}`)}
          renderInviteButton={renderInviteButton}
        />
      </PageContainer>
    </Page>
  );
}

export const ALLOWED_VIEWS: CollectionView[] = ["table", "list"];

export function CrmCompanyCollectionView({
  activeView,
  isPdsSyncEnabled,
  isLoading,
  paginationModel,
  setPaginationModel,
  sorting,
  setSorting,
  crmCompanies,
  totalCount,
  selectedCrmCompany,
  setSelectedCrmCompany,
  onItemClick,
  renderInviteButton,
}: {
  activeView: CollectionView;
  isPdsSyncEnabled?: boolean;
  isLoading?: boolean;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: (paginationModel: {
    page: number;
    pageSize: number;
  }) => void;
  sorting: OrganisationCrmOrganisationsSorting[];
  setSorting: React.Dispatch<
    React.SetStateAction<OrganisationCrmOrganisationsSorting[]>
  >;
  crmCompanies: CrmCompanies__CrmCompanyFragment[];
  totalCount: number;
  selectedCrmCompany?: { id: string } | null;
  setSelectedCrmCompany?: (
    crmCompany: CrmCompanies__CrmCompanyFragment | null
  ) => void;
  onItemClick?: (crmCompany: CrmCompanies__CrmCompanyFragment) => void;
  renderInviteButton?: (
    crmCompany: CrmCompanies__CrmCompanyFragment
  ) => React.ReactElement;
}) {
  const { t } = useTranslate([
    "CrmOrganisations",
    "CrmUsers",
    "Global",
    "CrmPageTopbar",
  ]);
  const { getFormattedDateTime } = useFormatting();

  const stateStore = useDataGridStateStore("CrmAll");

  const columns = React.useMemo(
    (): GridColDef<CrmCompanies__CrmCompanyFragment>[] => [
      {
        field: "organisationNumber",
        headerName: t("No.", {
          ns: "CrmOrganisations",
        }),
        flex: 1,
        minWidth: 100,
        sortable: true,
        renderCell: ({ row: crmCompany }) => crmCompany.number,
      },
      {
        field: "organisationName",
        headerName: t("Name", {
          ns: "CrmOrganisations",
        }),
        flex: 2,
        minWidth: 100,
        sortable: true,
        renderCell: ({ row: crmCompany }) => (
          <Stack
            direction={"row"}
            spacing={2}
            sx={{
              alignItems: "center",
            }}
          >
            <Avatar
              url={crmCompany.logo?.url}
              initials={crmCompany.title.substring(0, 2)}
              size="s"
              type="square"
            />
            <div>{crmCompany.title}</div>
          </Stack>
        ),
      },
      {
        field: "mainContact",
        headerName: t("Main Contact", {
          ns: "CrmOrganisations",
        }),
        flex: 2,
        minWidth: 100,
        sortable: false,
        renderCell: ({ row: crmCompany }) =>
          crmCompany.contactPerson?.fullname ?? "-",
      },
      {
        field: "tags",
        headerName: t("Tags", {
          ns: "CrmOrganisations",
        }),
        flex: 2,
        minWidth: 100,
        sortable: false,

        renderCell: ({ row: crmCompany }) => (
          <CrmTagChips
            tags={[...crmCompany.systemTags, ...crmCompany.customTags]}
            wrap={false}
          />
        ),
      },
      ...(renderInviteButton
        ? [
            {
              field: "linked",
              headerName: t("linked?", {
                ns: "CrmOrganisations",
              }),
              flex: 1.5,
              minWidth: 80,
              maxWidth: 140,
              sortable: false,
              renderCell: ({
                row: crmCompany,
              }: {
                row: CrmCompanies__CrmCompanyFragment;
              }) => renderInviteButton(crmCompany),
            },
          ]
        : []),
      {
        field: "duplicate",
        headerName: t("duplicate of", {
          ns: "CrmOrganisations",
        }),
        flex: 1.5,
        minWidth: 80,
        maxWidth: 140,
        sortable: false,
        renderCell: ({ row: crmCompany }) =>
          crmCompany.crmDuplicateOfId ? (
            <div
              style={{
                maxWidth: "80px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                backgroundColor: "red",
              }}
              title={crmCompany.crmDuplicateOfId}
            >
              {crmCompany.crmDuplicateOfId}
            </div>
          ) : null,
      },
      {
        field: "abcCategory",
        headerName: t("Priority", {
          ns: "CrmOrganisations",
        }),
        width: 100,
        sortable: false,
        renderCell: ({ row: crmCompany }) => (
          <OrganisationCategoryAvatar abcCategory={crmCompany.abcCategory} />
        ),
      },
      ...(isPdsSyncEnabled
        ? [
            {
              field: "lastPdsSyncAt",
              headerName: t("Last PDS sync", {
                ns: "CrmUsers",
              }),
              flex: 1,
              minWidth: 100,
              sortable: false,
              renderCell: ({
                row: crmCompany,
              }: {
                row: CrmCompanies__CrmCompanyFragment;
              }) => (
                <span className="sentry-mask">
                  {crmCompany.lastPdsSyncAt
                    ? getFormattedDateTime(crmCompany.lastPdsSyncAt)
                    : "—"}
                </span>
              ),
            },
          ]
        : []),
    ],
    [isPdsSyncEnabled, renderInviteButton, t, getFormattedDateTime]
  );

  const rowSelectionModel = React.useMemo(
    () =>
      ({
        type: "include",
        ids: new Set(selectedCrmCompany ? [selectedCrmCompany.id] : []),
      }) satisfies GridRowSelectionModel,
    [selectedCrmCompany]
  );

  switch (activeView) {
    case "table":
      return (
        <DataGrid
          stateStore={stateStore}
          density="standard"
          loading={isLoading}
          hideFooter={totalCount === 0}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={newPaginationModel => {
            setPaginationModel(newPaginationModel);
          }}
          pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
          rowCount={totalCount}
          sortModel={sorting.map(s => ({
            field: s.column,
            sort: s.direction,
          }))}
          onSortModelChange={newModel => {
            setSorting(
              newModel.map(({ field, sort }) => ({
                column: field as OrganisationCrmOrganisationsSortBy,
                direction: sort ?? "asc",
              }))
            );
          }}
          rows={crmCompanies}
          rowSelectionModel={rowSelectionModel}
          onRowClick={({ row: crmCompany }) => {
            if (onItemClick) {
              onItemClick(crmCompany);
            } else {
              setSelectedCrmCompany?.(
                selectedCrmCompany?.id === crmCompany.id ? null : crmCompany
              );
            }
          }}
        />
      );
    case "list":
      if (isLoading) return <LoadingSpinner />;
      if (totalCount === 0)
        return (
          <ErrorMessage
            message={t("There are no items to display", {
              ns: "Global",
            })}
          />
        );
      return (
        <Stack direction="column" spacing={1}>
          {crmCompanies.map(crmCompany => (
            <CardItem
              key={crmCompany.id}
              onClick={() => {
                if (onItemClick) {
                  onItemClick(crmCompany);
                } else {
                  setSelectedCrmCompany?.(
                    selectedCrmCompany?.id === crmCompany.id ? null : crmCompany
                  );
                }
              }}
              selected={selectedCrmCompany?.id === crmCompany.id}
            >
              <CrmCompanyListItem
                crmCompany={crmCompany}
                Action={
                  renderInviteButton
                    ? renderInviteButton(crmCompany)
                    : undefined
                }
              />
            </CardItem>
          ))}
          <TablePagination
            component="div"
            count={totalCount}
            page={paginationModel.page}
            onPageChange={(event, newPage) => {
              setPaginationModel({
                pageSize: paginationModel.pageSize,
                page: newPage,
              });
            }}
            rowsPerPage={paginationModel.pageSize}
            onRowsPerPageChange={event => {
              setPaginationModel({
                pageSize: parseInt(event.target.value, 10),
                page: 0,
              });
            }}
            labelRowsPerPage={t("Per page:", { ns: "Global" })}
          />
        </Stack>
      );
  }
}

function CrmAllFilterModal({
  filters,
  setFilters,
  resetFilters,
  handleClose,
  tags,
}: {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  resetFilters: () => void;
  handleClose: () => void;
  tags: string[];
}) {
  const { t } = useTranslate(["CrmOrganisations", "Global"]);

  const [filtersState, setFiltersState] = React.useState(filters);

  const { abcCategoryOptions } = useCrmOrganisationAbcCategories();

  return (
    <FilterModal
      title={t("Businesses & Groups Filters", {
        ns: "CrmOrganisations",
      })}
      handleClose={handleClose}
      handleApply={() => {
        setFilters(filtersState);
      }}
      handleReset={resetFilters}
    >
      <Typography variant="h3">
        {t("Filter by", {
          ns: "Global",
        })}
      </Typography>
      <CrmTagsAutocompleteInput
        inputLabel={t("Tags", { ns: "CrmOrganisations" })}
        options={tags}
        value={filtersState.filterByTagsAny ?? []}
        onChange={value => {
          setFiltersState(state => ({ ...state, filterByTagsAny: value }));
        }}
      />
      <SelectMultiple
        label={t("Priority", {
          ns: "CrmOrganisations",
        })}
        options={abcCategoryOptions}
        value={filtersState.filterAbcCategory ?? []}
        onChange={value => {
          setFiltersState(state => ({ ...state, filterAbcCategory: value }));
        }}
      />
    </FilterModal>
  );
}
